/**
 * TeamSystem Impact - Login Page
 * Split layout: immagine ambiente a sx, form login a dx
 */

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { CircularProgress } from '@mui/material';
import {
  Microsoft as MicrosoftIcon,
  AdminPanelSettings as AdminIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { loginRequest } from '../../config/msalConfig';
import { useAuthStore } from '../../store/authStore';
import { apiClient } from '../../api/client';
import { logger } from '@/config/logger';

const USE_MOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH !== 'false'; // default: true

logger.debug('Mock Auth Debug:', {
  VITE_USE_MOCK_AUTH: import.meta.env.VITE_USE_MOCK_AUTH,
  USE_MOCK_AUTH,
});

export const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();

  // Chi arriva da una pagina protetta (es. il consenso del connettore MCP) va
  // riportato lì, con i suoi parametri: prima si finiva sempre in dashboard e la
  // richiesta di autorizzazione andava persa.
  const destinazione = (location.state as { from?: string } | null)?.from || '/dashboard';
  const { setAuth, setLoading, isLoading } = useAuthStore();
  const [mockLoading, setMockLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      const msalResponse = await instance.loginPopup(loginRequest);
      const accessToken = msalResponse.accessToken;

      // Send token to backend for JWT exchange
      const backendResponse = await apiClient.post('/auth/login', { accessToken });
      const { jwt, refreshToken, utente } = backendResponse.data;
      setAuth(jwt, utente, refreshToken);
      navigate(destinazione, { replace: true });
    } catch (error) {
      logger.error('Errore login Microsoft:', error);
      setError(t('auth.loginErrorMicrosoft'));
      setLoading(false);
    }
  };

  const handleMockLogin = async (userType: 'admin' | 'contributore' | 'visualizzatore' | 'contributore2') => {
    try {
      setMockLoading(userType);
      setError(null);
      const response = await apiClient.post('/auth/dev-login', { userType });
      const { jwt, refreshToken, utente } = response.data;
      setAuth(jwt, utente, refreshToken);
      navigate(destinazione);
    } catch (error: any) {
      logger.error('Errore mock login:', error);
      setError(error.response?.data?.message || 'Errore durante il login mock');
      setMockLoading(null);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left: Background image */}
      <div className="hidden lg:flex lg:w-3/5 relative overflow-hidden">
        <img
          src="/login-bg.jpg"
          alt="Modern workspace"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <img
            src="/logo-teamsystem.png"
            alt="TeamSystem"
            className="w-16 h-16 mb-6 rounded-lg"
          />
          <h1 className="text-4xl font-bold mb-3">TeamSystem Impact</h1>
          <p className="text-lg text-white/80 max-w-md">
            {t('auth.tagline')}
          </p>
        </div>
      </div>

      {/* Right: Login form */}
      <div className="flex w-full lg:w-2/5 items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm space-y-8">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 lg:hidden">
            <img src="/logo-teamsystem.png" alt="TeamSystem" className="w-10 h-10 rounded-lg" />
            <span className="text-xl font-bold text-foreground">TeamSystem Impact</span>
          </div>

          {/* Header */}
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold tracking-tight text-foreground">
              {t('auth.login')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('auth.welcome')}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Mock Auth (dev only) */}
          {USE_MOCK_AUTH && (
            <>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                Modalità Sviluppo — Login rapido senza Azure AD
              </div>

              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-11"
                  onClick={() => handleMockLogin('admin')}
                  disabled={!!mockLoading}
                >
                  {mockLoading === 'admin' ? (
                    <CircularProgress size={16} />
                  ) : (
                    <AdminIcon sx={{ fontSize: 18 }} />
                  )}
                  <div className="text-left">
                    <div className="text-sm font-medium">Mario Rossi</div>
                    <div className="text-xs text-muted-foreground">Admin</div>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-11"
                  onClick={() => handleMockLogin('contributore')}
                  disabled={!!mockLoading}
                >
                  {mockLoading === 'contributore' ? (
                    <CircularProgress size={16} />
                  ) : (
                    <EditIcon sx={{ fontSize: 18 }} />
                  )}
                  <div className="text-left">
                    <div className="text-sm font-medium">Laura Bianchi</div>
                    <div className="text-xs text-muted-foreground">Contributore</div>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-11"
                  onClick={() => handleMockLogin('visualizzatore')}
                  disabled={!!mockLoading}
                >
                  {mockLoading === 'visualizzatore' ? (
                    <CircularProgress size={16} />
                  ) : (
                    <ViewIcon sx={{ fontSize: 18 }} />
                  )}
                  <div className="text-left">
                    <div className="text-sm font-medium">Giuseppe Verdi</div>
                    <div className="text-xs text-muted-foreground">Contributore</div>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-11"
                  onClick={() => handleMockLogin('contributore2')}
                  disabled={!!mockLoading}
                >
                  {mockLoading === 'contributore2' ? (
                    <CircularProgress size={16} />
                  ) : (
                    <EditIcon sx={{ fontSize: 18 }} />
                  )}
                  <div className="text-left">
                    <div className="text-sm font-medium">Gino Gialli</div>
                    <div className="text-xs text-muted-foreground">Contributore</div>
                  </div>
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">oppure</span>
                </div>
              </div>
            </>
          )}

          {/* Microsoft login */}
          <Button
            className="w-full h-11 gap-2"
            onClick={handleLogin}
            disabled={isLoading || !!mockLoading}
          >
            {isLoading ? (
              <CircularProgress size={16} sx={{ color: 'inherit' }} />
            ) : (
              <MicrosoftIcon sx={{ fontSize: 18 }} />
            )}
            {isLoading ? t('auth.loggingIn') : t('auth.loginWithMicrosoft')}
          </Button>

          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} TeamSystem S.p.A. — {t('auth.allRightsReserved')}
          </p>
        </div>
      </div>
    </div>
  );
};
